import { useMemo, useState, Fragment } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  InputAdornment,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import GroupIcon from "@mui/icons-material/Group";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import InboxIcon from "@mui/icons-material/Inbox";

function ListViewModal({
  isOpen,
  entityName = "Items",
  mainTitle = "List",
  mainMessage = "",
  onCancelText = "Close",
  items = [], // IMPORTANT!!!!: array, not {}
  onCancel,
  onSelect,
  enableSearch = true,
  maxHeight = 420,
}) {
  const [query, setQuery] = useState("");
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  const safeItems = Array.isArray(items) ? items : [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return safeItems;

    return safeItems.filter((x) => {
      // it supports: { key, value } OR { id, name } if we change it later.
      const key = String(x?.key ?? x?.id ?? "").toLowerCase();
      const value = String(x?.value ?? x?.name ?? "").toLowerCase();
      const secondary = String(x?.secondary ?? "").toLowerCase();
      return key.includes(q) || value.includes(q) || secondary.includes(q);
    });
  }, [safeItems, query]);

  const title = `${entityName} ${mainTitle}`.trim();

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/[\s._@-]+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <Dialog
      open={!!isOpen}
      onClose={onCancel}
      fullWidth
      maxWidth="sm"
      aria-labelledby="list-view-modal-title"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${primary} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "#fff",
          px: 3,
          pt: 3,
          pb: 2.5,
          position: "relative",
        }}
      >
        <Tooltip title="Close">
          <IconButton
            onClick={onCancel}
            aria-label="close"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: "rgba(255,255,255,0.8)",
              "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              backgroundColor: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <GroupIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              id="list-view-modal-title"
              variant="h6"
              sx={{ fontWeight: 600, lineHeight: 1.3, textTransform: "capitalize" }}
            >
              {title}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
          <Chip
            size="small"
            label={`${safeItems.length} ${safeItems.length === 1 ? "member" : "members"}`}
            sx={{
              backgroundColor: "rgba(255,255,255,0.2)",
              color: "#fff",
              fontWeight: 500,
              fontSize: "0.75rem",
              height: 24,
            }}
          />
          {!!mainMessage && (
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.8rem" }}>
              {mainMessage}
            </Typography>
          )}
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        {/* Search */}
        {enableSearch && safeItems.length > 6 && (
          <Box sx={{ mb: 2 }}>
            <TextField
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${entityName.toLowerCase()}...`}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
                ...(query && {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setQuery("")}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: alpha(primary, 0.04),
                  "&:hover fieldset": { borderColor: alpha(primary, 0.3) },
                  "&.Mui-focused fieldset": { borderColor: primary },
                },
              }}
            />
          </Box>
        )}

        {/* List */}
        {filtered.length === 0 ? (
          <Box
            sx={{
              py: 5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            {query ? (
              <SearchOffIcon sx={{ fontSize: 40, color: "text.disabled" }} />
            ) : (
              <InboxIcon sx={{ fontSize: 40, color: "text.disabled" }} />
            )}
            <Typography variant="body2" color="text.secondary">
              {query ? "No results match your search." : "No items to display."}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              maxHeight,
              overflow: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <List disablePadding>
              {filtered.map((item, idx) => {
                const name = item?.value ?? item?.name ?? "";
                return (
                  <Fragment key={item?.key ?? item?.id ?? `${name}-${idx}`}>
                    <ListItemButton
                      onClick={() => onSelect?.(item)}
                      sx={{
                        py: 1.25,
                        px: 2,
                        "&:hover": {
                          backgroundColor: alpha(primary, 0.06),
                        },
                        "&:hover .view-icon": {
                          opacity: 1,
                        },
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 44 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            backgroundColor: alpha(primary, 0.12),
                            color: primary,
                          }}
                        >
                          {getInitials(name)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={name}
                        primaryTypographyProps={{
                          noWrap: true,
                          sx: { fontWeight: 500, fontSize: "0.875rem" },
                        }}
                      />
                      {onSelect && (
                        <OpenInNewIcon
                          className="view-icon"
                          sx={{
                            fontSize: 16,
                            color: "text.disabled",
                            opacity: 0,
                            transition: "opacity 0.2s",
                            ml: 1,
                          }}
                        />
                      )}
                    </ListItemButton>
                    {idx !== filtered.length - 1 && <Divider component="li" />}
                  </Fragment>
                );
              })}
            </List>
          </Box>
        )}

        {/* Filtered count hint */}
        {query && filtered.length > 0 && filtered.length !== safeItems.length && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1, textAlign: "center" }}
          >
            Showing {filtered.length} of {safeItems.length}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button
          onClick={onCancel}
          variant="contained"
          disableElevation
          sx={{
            textTransform: "none",
            borderRadius: 2,
            minWidth: 100,
            fontWeight: 500,
          }}
        >
          {onCancelText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ListViewModal;